import type { GetTokenSilentlyOptions, IdToken } from '@auth0/auth0-react';

/** Seconds before `exp` at which the cached ID token is treated as stale. */
const EXPIRY_LEEWAY_SECONDS = 60;

export interface IdTokenSdk {
  getIdTokenClaims: () => Promise<IdToken | undefined>;
  getAccessTokenSilently: (
    options?: GetTokenSilentlyOptions
  ) => Promise<unknown>;
}

const isStale = (claims: IdToken | undefined): boolean => {
  const exp = claims?.exp;
  if (typeof exp !== 'number') return false;
  return exp <= Date.now() / 1000 + EXPIRY_LEEWAY_SECONDS;
};

/**
 * Return the raw Auth0 ID token, refreshing it first when the cached copy is
 * expired or about to be. The SPA SDK hands back cached ID tokens without
 * checking their expiry, while the server enforces `exp` — without this, a
 * session idle past the token lifetime 401s until a manual re-login.
 * `getAccessTokenSilently({ cacheMode: 'off' })` forces a refresh-token
 * round-trip that rotates a fresh ID token into the cache.
 *
 * Never throws: resolves undefined when unauthenticated or when both the
 * cache and the silent refresh fail — callers decide whether that is fatal.
 * Preview mode is unaffected: the mock claims carry no `exp`, so no refresh
 * is ever attempted.
 */
export async function fetchFreshIdToken(
  sdk: IdTokenSdk,
  { forceRefresh = false }: { forceRefresh?: boolean } = {}
): Promise<string | undefined> {
  try {
    let claims = forceRefresh ? undefined : await sdk.getIdTokenClaims();
    if (forceRefresh || isStale(claims)) {
      try {
        await sdk.getAccessTokenSilently({ cacheMode: 'off' });
      } catch {
        // Silent refresh can fail (refresh token expired/revoked, offline).
        // Fall back to whatever the cache holds; the caller's 401 handling
        // (retry or login redirect) owns the next step.
      }
      claims = await sdk.getIdTokenClaims();
    }
    return claims?.__raw;
  } catch {
    return undefined;
  }
}
